import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext(null);

// Catalog of named theme palettes shown in Settings. Exactly five themes
// per product spec: "Minimal White" is the free baseline; the other four
// are Premium-locked. A legacy "default" id may still be in some users'
// localStorage — it's treated as "minimal-white" in the picker but the
// useEffect below leaves the actual styling alone (no data-theme) so we
// don't visually surprise long-time users.
export const PALETTES = [
  { id: "minimal-white", label: "Minimal White", premium: false, hue: "#71717A" },
  { id: "neet-green", label: "NEET Green", premium: true, hue: "#10B981" },
  { id: "jee-blue", label: "JEE Blue", premium: true, hue: "#3B82F6" },
  { id: "midnight-black", label: "Midnight Black", premium: true, hue: "#111827" },
  { id: "purple-aura", label: "Purple Aura", premium: true, hue: "#A855F7" },
];

const PALETTE_KEY = "syllox-palette";

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => localStorage.getItem("syllox-theme") || "system");
  const [palette, setPaletteState] = useState(() => {
    // New users default to Minimal White (the free baseline).
    // Legacy users may have any stored id — keep it as-is; the picker handles
    // unknown ids gracefully by simply leaving nothing highlighted.
    return localStorage.getItem(PALETTE_KEY) || "minimal-white";
  });

  // Apply light/dark
  useEffect(() => {
    const root = document.documentElement;
    const apply = (m) => {
      const isDark = m === "dark" || (m === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      root.classList.toggle("dark", isDark);
    };
    apply(mode);
    localStorage.setItem("syllox-theme", mode);
    if (mode === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => apply("system");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [mode]);

  // Apply named palette via [data-theme="..."] (no attribute for default)
  useEffect(() => {
    const root = document.documentElement;
    if (palette && palette !== "default") {
      root.setAttribute("data-theme", palette);
    } else {
      root.removeAttribute("data-theme");
    }
    localStorage.setItem(PALETTE_KEY, palette);
  }, [palette]);

  const setPalette = useCallback((next) => {
    const entry = PALETTES.find((p) => p.id === next);
    if (!entry) return;
    setPaletteState(entry.id);
  }, []);

  const value = useMemo(
    () => ({ mode, setMode, palette, setPalette, palettes: PALETTES }),
    [mode, palette, setPalette]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
